import { useEffect, useMemo, useRef, useState } from "react";
import type { UserRole } from "./auth-types";
import {
  queryConferences,
  type ConferenceFilter,
  type ConferenceSort,
} from "./conference-directory-model";
import { RefreshIcon, SearchIcon } from "./icons";
import { ConferenceBrandImage } from "./ConferenceBrandImage";
import { RiyalAmount } from "./RiyalAmount";
import {
  type ConferenceDirectoryClient,
  type PortfolioConferenceSummary,
  type PortfolioOverview,
} from "./portfolio-overview-api";
import { useLocale, type MessageKey } from "./localization";
import {
  PORTFOLIO_ACCESS_ERROR_MESSAGES,
  portfolioErrorMessageKey,
  portfolioFailureState,
  type PortfolioFailureState,
} from "./portfolio-api-errors";

interface ConferenceDirectoryPanelProps {
  readonly client: ConferenceDirectoryClient;
  readonly role: UserRole;
}

type DirectoryState =
  | { readonly kind: "loading" }
  | { readonly kind: "loaded"; readonly overview: PortfolioOverview }
  | PortfolioFailureState;

function ConnectionLabel({
  value,
}: {
  readonly value: PortfolioConferenceSummary["connectionStatus"];
}) {
  const { t } = useLocale();
  const labels = {
    connected: t("conference.connection.connected"),
    degraded: t("conference.connection.degraded"),
    never_synced: t("conference.connection.neverSynchronized"),
    upgrade_required: t("conference.connection.upgradeRequired"),
    disabled: t("conference.connection.disabled"),
  } as const;
  return (
    <span className={`connection-label connection-label--${value}`}>
      <span aria-hidden="true" />
      {labels[value]}
    </span>
  );
}

function DirectoryLoading() {
  const { t } = useLocale();
  return (
    <div
      className="directory-loading"
      aria-label={t("directory.loading")}
      aria-busy="true"
    >
      <div className="directory-loading__toolbar" />
      <div className="loading-table">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

export function ConferenceDirectoryPanel({
  client,
  role,
}: ConferenceDirectoryPanelProps) {
  const { date, dateRange, locale, number, t } = useLocale();
  const [state, setState] = useState<DirectoryState>({ kind: "loading" });
  const [filter, setFilter] = useState<ConferenceFilter>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<ConferenceSort>("event-date");
  const [revision, setRevision] = useState(0);
  const [disableTarget, setDisableTarget] =
    useState<PortfolioConferenceSummary | null>(null);
  const [disableBusy, setDisableBusy] = useState(false);
  const [disableError, setDisableError] = useState<MessageKey | null>(null);
  const confirmationRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = confirmationRef.current;
    if (!disableTarget || !dialog) return;
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
    return () => {
      if (dialog.open && typeof dialog.close === "function") dialog.close();
    };
  }, [disableTarget]);

  useEffect(() => {
    const abortController = new AbortController();
    setState({ kind: "loading" });
    void client
      .load(abortController.signal)
      .then((overview) => setState({ kind: "loaded", overview }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setState(portfolioFailureState(error));
      });
    return () => abortController.abort();
  }, [client, revision]);

  const visible = useMemo(
    () =>
      state.kind === "loaded"
        ? queryConferences(state.overview.conferences, {
            filter,
            search,
            sort,
            locale,
          })
        : [],
    [filter, locale, search, sort, state],
  );

  const formatCount = (value: number | null) =>
    value === null ? t("common.unavailable") : number(value);
  const formatMoney = (value: number | null) => <RiyalAmount minor={value} />;

  async function confirmDisable() {
    if (!disableTarget || disableBusy) return;
    setDisableBusy(true);
    setDisableError(null);
    try {
      await client.disableConference(disableTarget.conferenceId);
      setDisableTarget(null);
      setRevision((value) => value + 1);
    } catch (error) {
      setDisableError(
        portfolioErrorMessageKey(error, "directory.disable.error"),
      );
    } finally {
      setDisableBusy(false);
    }
  }

  if (state.kind === "loading") return <DirectoryLoading />;
  if (state.kind === "error") {
    return (
      <section className="overview-state" role="alert">
        <h2>
          {state.sessionExpired
            ? t("session.expired.title")
            : state.accessError
              ? t("access.denied.title")
              : t("directory.error.title")}
        </h2>
        <p>
          {state.sessionExpired
            ? t("session.expired.description")
            : state.accessError
              ? t(PORTFOLIO_ACCESS_ERROR_MESSAGES[state.accessError])
              : t("directory.error.description")}
        </p>
        {!state.sessionExpired && !state.accessError ? (
          <button
            className="button button--secondary overview-state__action"
            type="button"
            onClick={() => setRevision((value) => value + 1)}
          >
            <RefreshIcon />
            {t("common.tryAgain")}
          </button>
        ) : null}
      </section>
    );
  }

  return (
    <>
      <section
        className="directory-toolbar"
        aria-label={t("directory.controls.ariaLabel")}
      >
        <div
          className="directory-filters"
          aria-label={t("directory.filters.ariaLabel")}
        >
          {(["all", "active", "upcoming", "ended"] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {value === "all"
                ? t("directory.filter.all")
                : value === "active"
                  ? t("conference.lifecycle.active")
                  : value === "upcoming"
                    ? t("conference.lifecycle.upcoming")
                    : t("conference.lifecycle.ended")}
            </button>
          ))}
        </div>
        <label className="directory-search">
          <span className="visually-hidden">{t("directory.search.label")}</span>
          <SearchIcon />
          <input
            name="conference-search"
            type="search"
            autoComplete="off"
            spellCheck={false}
            dir="auto"
            value={search}
            placeholder={t("directory.search.placeholder")}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </label>
        <label className="directory-sort">
          <span>{t("directory.sort.label")}</span>
          <select
            name="conference-sort"
            value={sort}
            onChange={(event) =>
              setSort(event.currentTarget.value as ConferenceSort)
            }
          >
            <option value="event-date">{t("directory.sort.eventDate")}</option>
            <option value="name">{t("directory.sort.name")}</option>
            <option value="last-synchronized">
              {t("directory.sort.lastSynchronized")}
            </option>
          </select>
        </label>
      </section>

      {visible.length === 0 ? (
        <section className="overview-state">
          <h2>{t("directory.empty.title")}</h2>
          <p>{t("directory.empty.description")}</p>
        </section>
      ) : (
        <div className="directory-table-wrap">
          <table className="directory-table">
            <caption>{t("directory.table.caption")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("table.conference")}</th>
                <th scope="col">{t("table.dates")}</th>
                <th scope="col">{t("table.status")}</th>
                <th scope="col">{t("table.registrations")}</th>
                <th scope="col">{t("table.collected")}</th>
                <th scope="col">{t("table.visitors")}</th>
                <th scope="col">{t("table.lastSynchronized")}</th>
                <th scope="col">{t("table.connection")}</th>
                <th scope="col">{t("table.action")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((conference) => (
                <tr key={conference.conferenceId}>
                  <td data-label={t("table.conference")}>
                    <div className="directory-conference">
                      <ConferenceBrandImage
                        conferenceId={conference.conferenceId}
                        conferenceName={conference.name}
                        configuredLogo={
                          conference.iconUrl ?? conference.logoUrl
                        }
                        compact
                        width="42"
                        height="42"
                      />
                      <strong>
                        <bdi>{conference.name}</bdi>
                      </strong>
                    </div>
                  </td>
                  <td data-label={t("table.dates")}>
                    <bdi>
                      {dateRange(conference.startsAt, conference.endsAt, {
                        timeZone: "Asia/Riyadh",
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </bdi>
                  </td>
                  <td data-label={t("table.status")}>
                    <span
                      className={`directory-lifecycle directory-lifecycle--${conference.lifecycle}`}
                    >
                      {conference.lifecycle === "active"
                        ? t("conference.lifecycle.active")
                        : conference.lifecycle === "upcoming"
                          ? t("conference.lifecycle.upcoming")
                          : t("conference.lifecycle.ended")}
                    </span>
                  </td>
                  <td
                    className="numeric-cell"
                    data-label={t("table.registrations")}
                  >
                    <bdi>{formatCount(conference.registrationsTotal)}</bdi>
                  </td>
                  <td
                    className="numeric-cell"
                    data-label={t("table.collected")}
                  >
                    <bdi>{formatMoney(conference.netCollectedCents)}</bdi>
                  </td>
                  <td className="numeric-cell" data-label={t("table.visitors")}>
                    <bdi>{formatCount(conference.visitorsThisMonth)}</bdi>
                  </td>
                  <td data-label={t("table.lastSynchronized")}>
                    <bdi>
                      {conference.lastSuccessfulSyncAt
                        ? date(conference.lastSuccessfulSyncAt, {
                            timeZone: "Asia/Riyadh",
                            dateStyle: "medium",
                            timeStyle: "short",
                          })
                        : t("sync.notSynchronized")}
                    </bdi>
                  </td>
                  <td data-label={t("table.connection")}>
                    <ConnectionLabel value={conference.connectionStatus} />
                  </td>
                  <td data-label={t("table.action")}>
                    <div className="directory-actions">
                      <a
                        className="button button--primary button--compact"
                        href={`#/conferences/${conference.conferenceId}`}
                      >
                        {t("directory.action.open")}
                      </a>
                      {role === "admin" &&
                      conference.connectionStatus !== "disabled" ? (
                        <button
                          className="button button--quiet button--compact"
                          type="button"
                          onClick={() => {
                            setDisableError(null);
                            setDisableTarget(conference);
                          }}
                        >
                          {t("directory.action.disable")}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {disableTarget ? (
        <dialog
          ref={confirmationRef}
          className="confirmation-dialog"
          aria-labelledby="disable-conference-title"
          onCancel={(event) => {
            event.preventDefault();
            if (!disableBusy) setDisableTarget(null);
          }}
        >
          <h2 id="disable-conference-title">{t("directory.disable.title")}</h2>
          <p>
            <bdi>{disableTarget.name}</bdi>{" "}
            {t("directory.disable.descriptionSuffix")}
          </p>
          {disableError ? (
            <p className="form-message form-message--error" role="alert">
              {t(disableError)}
            </p>
          ) : null}
          <div className="confirmation-dialog__actions">
            <button
              className="button button--quiet"
              type="button"
              disabled={disableBusy}
              onClick={() => setDisableTarget(null)}
            >
              {t("common.cancel")}
            </button>
            <button
              className="button button--danger"
              type="button"
              disabled={disableBusy}
              onClick={() => void confirmDisable()}
            >
              {disableBusy
                ? t("directory.disable.busy")
                : t("directory.disable.confirm")}
            </button>
          </div>
        </dialog>
      ) : null}
    </>
  );
}
